import { motion } from 'framer-motion';
import React, { useRef, useState, useEffect } from 'react';
import { FaDocker, FaGlobe, FaMobile, FaNodeJs, FaPython, FaReact, FaRobot } from 'react-icons/fa';
import { SiAmazonaws, SiFlutter, SiGo, SiMongodb } from 'react-icons/si';
import './Landing.css';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { getApiUrl } from '../utils/api';

const BUTTON_TEXT="BOOK A FREE CALL"

const Landing = () => {
  const headlineRef = useRef(null);
  const landingContentRef = useRef(null);
  const button_text=useRef(null);

  const [heroData, setHeroData] = useState({
    title: "Your Vision Our Code",
    description: "We specialize in builds smart digital products that help businesses grow. From AI-powered apps to powerful backend systems, we turn ideas into reality. Our solutions are seamless, scalable, and designed for the future.",
    cards: [
      { title: "Ai Agents", description: "Create intelligent AI agents that automate tasks and enhance productivity." },
      { title: "Website", description: "Create stunning, responsive websites that engage your audience." },
      { title: "Mobile Apps", description: "Develop cross-platform mobile applications for iOS and Android." }
    ]
  });

  useEffect(() => {
    const url = getApiUrl('/api/heroes');
    fetch(url)
      .then(res => res.json())
      .then(data => {
        console.log("Fetched heroes data:", data);
        let hero = null;
        if (Array.isArray(data) && data.length > 0) {
          hero = data[0];
        } else if (data && Array.isArray(data.data) && data.data.length > 0) {
          hero = data.data[0];
        }
        
        if (hero) {
          setHeroData({
            title: hero.title || heroData.title,
            description: hero.description || heroData.description,
            cards: hero.cards || heroData.cards
          });
        }
      })
      .catch(err => console.error("Error fetching heroes:", err));
  }, []);

  useGSAP(() => {
    gsap.fromTo(
      '.headline-char',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.5, stagger: 0.04, ease: 'power2.out', delay: 0.2 }
    );
  }, { scope: headlineRef });

  useGSAP(() => {
    gsap.fromTo(
      '.buttoText',
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 1, stagger: 1, ease: 'power2.out', delay: 0.2 }
    );
  }, { scope: button_text });


  useGSAP(() => {
    gsap.from('.pTag p', {
      y: 30,
      opacity: 0,
      duration: 0.8,
      stagger: 0.2,
      ease: 'power2.out',
      delay: 0.6,
    });
  }, { scope: landingContentRef });
  

  const FloatingIcon = ({ Icon, position, color, delay = '0s' }) => {
    return (
      <div
        className="floating-icon"
        style={{
          position: 'absolute',
          ...position,
          color: color,
          zIndex: 10,
          filter: 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.4))',
          transition: 'all 0.3s ease',
          fontSize: '52px',
          pointerEvents: 'auto',
          animationDelay: delay,
        }}
      >
        <Icon />
      </div>
    );
  };


  // Split description into sentences to preserve staggering animation
  const descriptionParagraphs = heroData.description.split('.').filter(s => s.trim() !== '').map(s => s.trim() + '.');

  return (
    <>
      <section id="landing" className="landing">
        {/* Background decorative elements */}
        {/* Floating tech stack logos above shapes */}
        <div className="floating-icons-container">
          {/* Left side icons - 4 icons vertically aligned */}
          <FloatingIcon
            Icon={FaReact}
            position={{ top: '16%', left: '11%' }}
            color="#61DAFB"
            delay="0s"
          />
          <FloatingIcon
            Icon={SiGo}
            position={{ top: '41%', left: '16%' }}
            color="#00ADD8"
            delay="0.5s"
          />
          <FloatingIcon
            Icon={FaPython}
            position={{ top: '70%', left: '9%' }}
            color="#3776ab"
            delay="1s"
          />
          <FloatingIcon
            Icon={FaDocker}
            position={{ top: '35%', left: '4%' }}
            color="#2496ed"
            delay="1.5s"
          />

          {/* Right side icons - 4 icons vertically aligned */}
          <FloatingIcon
            Icon={SiMongodb}
            position={{ top: '26%', right: '16%' }}
            color="#47A248"
            delay="2s"
          />
          <FloatingIcon
            Icon={FaNodeJs}
            position={{ top: '41%', right: '9%' }}
            color="#339933"
            delay="2.5s"
          />
          <FloatingIcon
            Icon={SiAmazonaws}
            position={{ top: '16%', right: '6%' }}
            color="#FF9900"
            delay="3s"
          />
          <FloatingIcon
            Icon={SiFlutter}
            position={{ top: '70%', right: '5%' }}
            color="#02569B"
            delay="3.5s"  
          />
        </div>

        <div ref={landingContentRef} className="landing-content">
          <div className="landing-content-inner">
            <h1 ref={headlineRef} className="main-headline">
              {heroData.title.split('').map((char, i) => (
                <span key={i} className="headline-char">
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </h1>
          </div>


          <div className="subtitle pTag">
            {descriptionParagraphs.map((para, i) => (
              <p key={i} className="subtitle">{para}</p>
            ))}
          </div>

          <motion.button
            className="cta-button"
            //  variants={itemVariants}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            ref={button_text}
          >
          <div className="buttoText" >
          {BUTTON_TEXT.split('').map((char, i) => (
                <span key={i} className="Button_Span">
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
          </div>
          </motion.button>

        </div>

        {/* Service Cards Section */}
        <div className="service-cards-container-1">
          <div className="service-card-1">
            <div className="card-header-1">
              <FaRobot className="card-icon-1" />
              <h3 className="card-title-1">{heroData.cards[0]?.title || "Ai Agents"}</h3>
              {/* <FaArrowRight className="card-arrow-1" /> */}
            </div>
            <p className="card-description-1">
              {heroData.cards[0]?.description || "Create intelligent AI agents that automate tasks and enhance productivity."}
            </p>
          </div>

          <div className="service-card-1">
            <div className="card-header-1">
              <FaGlobe className="card-icon-1" />
              <h3 className="card-title-1">{heroData.cards[1]?.title || "Website"}</h3>
              {/* <FaArrowRight className="card-arrow-1" /> */}
            </div>
            <p className="card-description-1">
              {heroData.cards[1]?.description || "Create stunning, responsive websites that engage your audience."}
            </p>
          </div>

          <div className="service-card-1">
            <div className="card-header-1">
              <FaMobile className="card-icon-1" />
              <h3 className="card-title-1">{heroData.cards[2]?.title || "Mobile Apps"}</h3>
              {/* <FaArrowRight className="card-arrow-1" /> */}
            </div>
            <p className="card-description-1">
              {heroData.cards[2]?.description || "Develop cross-platform mobile applications for iOS and Android."}
            </p>
          </div>
        </div>

      </section>
    </>
  );
};

export default Landing;

